import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtM } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Logo, TopNav } from "@/pages/Marketing";
import { SiteFooter } from "@/pages/Legal";
import { Check, Loader2, ShieldCheck } from "lucide-react";

export function PricingPage() {
  const [tiers, setTiers] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { api.get("/pricing").then((r) => setTiers(r.data.tiers)).catch(() => {}); }, []);
  const buy = async (tier) => {
    if (!user) { nav("/signup"); return; }
    if (user.kyc_status !== "verified") { nav("/kyc"); return; }
    try {
      const { data } = await api.post("/payments/checkout", { tier, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { alert(e.response?.data?.detail || "Checkout failed"); }
  };
  const priceLabel = (t) => {
    if (t.id === "free") return "$0";
    if (t.id === "tier3") return t.price_display || "$99/mo billed yearly";
    return `$${t.price}`;
  };
  const ctaLabel = (t) => {
    if (t.id === "free") return "Start free";
    if (t.id === "tier3") return "Go Unlimited — $1,188/yr";
    return `Choose ${t.name.split(" ")[0]} — $${t.price}`;
  };
  return (
    <div className="bg-white min-h-screen"><TopNav />
      <div className="relative overflow-hidden">
        <div className="hero-glow-orb w-[520px] h-[380px] bg-[#F5C86B]/50 top-[-160px] left-1/2 -translate-x-1/2" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-12">
          <div className="anim-fade-up inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.16em] text-[#174C82] bg-[#FFF8EB] border border-[#F5C86B] rounded-full px-3 py-1.5">PAY ONCE · OR GO UNLIMITED YEARLY</div>
          <h1 className="anim-fade-up anim-delay-1 font-display text-[34px] sm:text-[46px] font-bold tracking-tight mt-4">Pricing that scales with your raise<span className="text-[#174C82]">.</span></h1>
          <p className="anim-fade-up anim-delay-2 text-slate-500 mt-2 text-[16px]">Start with a free verified ballpark. Pay once per project — or unlock <b className="text-slate-700">unlimited valuations at $99/mo, billed yearly ($1,188/yr)</b>.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {tiers.map((t, i) => (
              <div key={t.id} className={`relative card-vs card-lift p-6 anim-fade-up anim-delay-${Math.min(i + 1, 4)} ${t.id === "tier2" ? "card-glow" : ""} ${t.id === "tier3" ? "ring-2 ring-emerald-400" : ""}`}>
                {t.id === "tier2" && <div className="badge-bestseller absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold text-white rounded-full px-3 py-1 whitespace-nowrap">MOST POPULAR</div>}
                {t.id === "tier3" && <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded-full px-3 py-1 whitespace-nowrap">BEST VALUE · YEARLY</div>}
                <div className="font-semibold mt-2">{t.name}</div>
                <div className={`font-mono-num font-semibold mt-1 ${t.id === "tier3" ? "text-[22px]" : "text-[30px]"}`}>{priceLabel(t)}</div>
                {t.id === "tier3" && <div className="font-mono-num text-[13px] text-slate-500">=$1,188 per year · unlimited valuations</div>}
                <div className="text-[12px] text-slate-500 mt-0.5">{t.pages} · {t.model === "subscription-yearly" ? "yearly subscription" : t.model}</div>
                <ul className="mt-4 space-y-1.5 text-[13px] text-slate-600">{t.features.map((f) => <li key={f} className="flex gap-1.5"><span className="w-[18px] h-[18px] rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5"><Check size={11} className="text-emerald-700" /></span>{f}</li>)}</ul>
                <button onClick={() => (t.id === "free" ? nav(user ? "/app" : "/signup") : buy(t.id))} className="btn-primary-vs w-full h-11 mt-5 font-semibold text-[14px]" data-testid={`pricing-buy-${t.id}`}>{ctaLabel(t)}</button>
              </div>
            ))}
          </div>
          <div className="card-vs p-5 mt-6 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="font-semibold text-[15px]">Why yearly for Unlimited?</div>
              <div className="text-[13px] text-slate-500 mt-0.5">At $99/mo the per-transaction economics only work annually — one $1,188 charge, unlimited 5-method valuations, projections, SEC packs and white-label exports for 12 months. Cheaper than a single consultant day.</div>
            </div>
            <div className="font-mono-num text-[15px] font-semibold text-[#174C82] bg-[#FFF8EB] border border-[#F5C86B] rounded-xl px-4 py-3">$1,188/yr = $99/mo effective</div>
          </div>
          <div className="text-[13px] text-slate-500 mt-5 flex items-center gap-2"><ShieldCheck size={15} /> Secure checkout by Stripe · all major cards accepted · tax calculated at checkout.</div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

export function AuthPage({ mode }) {
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [f, setF] = useState({ email: "", password: "", full_name: "", company_name: "", role: "founder", jurisdiction: "Delaware, US", tax_id: "", phone: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (mode === "login") { await login(f.email, f.password); nav("/app"); }
      else { await register(f); nav("/kyc"); }
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="bg-[#F8FAFC] min-h-screen"><TopNav />
      <div className="mx-auto max-w-[520px] px-4 py-12">
        <div className="card-vs p-7">
          <div className="overflow-hidden"><Logo size="sm" /></div>
          <h1 className="font-display text-[26px] font-bold mt-4">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-slate-500 text-[14px] mt-1">{mode === "login" ? "Sign in to your dealroom." : "Registration + KYC verification required before any valuation."}</p>
          <form onSubmit={submit} className="mt-5 space-y-3">
            {mode === "signup" && (<>
              <input className="w-full h-11 rounded-lg border border-slate-300 px-3" placeholder="Full name" value={f.full_name} onChange={set("full_name")} required data-testid="signup-name-input" />
              <div className="grid grid-cols-2 gap-3">
                <input className="h-11 rounded-lg border border-slate-300 px-3" placeholder="Company legal name" value={f.company_name} onChange={set("company_name")} required data-testid="signup-company-input" />
                <input className="h-11 rounded-lg border border-slate-300 px-3" placeholder="Tax ID (EIN)" value={f.tax_id} onChange={set("tax_id")} required data-testid="signup-taxid-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select className="h-11 rounded-lg border border-slate-300 px-3" value={f.role} onChange={set("role")} data-testid="signup-role-select"><option value="founder">Founder</option><option value="investor">Investor</option><option value="advisor">Advisor</option></select>
                <input className="h-11 rounded-lg border border-slate-300 px-3" placeholder="Phone / MFA number" value={f.phone} onChange={set("phone")} data-testid="signup-phone-input" />
              </div>
            </>)}
            <input className="w-full h-11 rounded-lg border border-slate-300 px-3" placeholder="Business email" type="email" value={f.email} onChange={set("email")} required data-testid={`${mode}-email-input`} />
            <input className="w-full h-11 rounded-lg border border-slate-300 px-3" placeholder="Password" type="password" value={f.password} onChange={set("password")} required data-testid={`${mode}-password-input`} />
            {err && <div className="text-red-600 text-[13px]" role="alert">{err}</div>}
            <button disabled={busy} className="btn-primary-vs w-full h-12 font-semibold flex items-center justify-center gap-2" data-testid={`${mode}-submit-button`}>{busy && <Loader2 className="animate-spin" size={17} />}{mode === "login" ? "Sign in" : "Register & continue to KYC"}</button>
          </form>
          {mode === "login" && <div className="text-[13px] text-right mt-2"><Link to="/forgot-password" className="text-[#174C82] font-semibold" data-testid="forgot-password-link">Forgot password?</Link></div>}
          <div className="text-[13px] text-slate-500 mt-4">{mode === "login" ? <>No account? <Link to="/signup" className="text-[#174C82] font-semibold">Register</Link></> : <>Have an account? <Link to="/login" className="text-[#174C82] font-semibold">Sign in</Link></>}</div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

export function KycPage() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ legal_entity_name: "", jurisdiction: "Delaware, US", tax_id: "", business_domain: "", owner_name: "", attest: false });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const status = user?.kyc_status;
  useEffect(() => {
    if (user) {
      setF((p) => ({ ...p, legal_entity_name: p.legal_entity_name || user.company_hint || "", jurisdiction: user.jurisdiction_hint || p.jurisdiction, owner_name: p.owner_name || user.full_name || "" }));
      if (status === "verified") nav("/app");
    }
  }, [user, status]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submitDetails = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.post("/kyc/submit", f); await refresh(); nav("/app"); }
    catch (e2) { setErr(typeof e2.response?.data?.detail === "string" ? e2.response.data.detail : JSON.stringify(e2.response?.data?.detail || "Could not save details")); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#F8FAFC] min-h-screen"><TopNav />
      <div className="mx-auto max-w-[620px] px-4 py-12">
        <div className="card-vs p-7">
          <div className="text-[12px] font-bold tracking-widest text-[#174C82]">BUSINESS VERIFICATION</div>
          <h1 className="font-display text-[26px] font-bold mt-2">Your business entity</h1>
          <p className="text-slate-500 text-[14px] mt-1">We collect baseline business details so we can issue your entity's one free ballpark valuation. Document-based identity verification is temporarily paused — your attested details are enough to get started today.</p>
          <form onSubmit={submitDetails} className="mt-5 space-y-3">
            <input className="w-full h-11 rounded-lg border border-slate-300 px-3" placeholder="Legal entity name" value={f.legal_entity_name} onChange={set("legal_entity_name")} required data-testid="kyc-entity-input" />
            <div className="grid grid-cols-2 gap-3">
              <input className="h-11 rounded-lg border border-slate-300 px-3" placeholder="Jurisdiction" value={f.jurisdiction} onChange={set("jurisdiction")} required data-testid="kyc-jurisdiction-input" />
              <input className="h-11 rounded-lg border border-slate-300 px-3" placeholder="Tax ID / EIN" value={f.tax_id} onChange={set("tax_id")} required data-testid="kyc-taxid-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className="h-11 rounded-lg border border-slate-300 px-3" placeholder="Business domain (acme.com)" value={f.business_domain} onChange={set("business_domain")} data-testid="kyc-domain-input" />
              <input className="h-11 rounded-lg border border-slate-300 px-3" placeholder="Beneficial owner" value={f.owner_name} onChange={set("owner_name")} data-testid="kyc-owner-input" />
            </div>
            <label className="flex gap-2 items-start text-[13px] text-slate-600"><input type="checkbox" checked={f.attest} onChange={set("attest")} className="mt-1" data-testid="kyc-attest-checkbox" /> I attest I am authorized to represent this entity and that the information above is accurate, and consent to audit logging.</label>
            {err && <div className="text-red-600 text-[13px]" role="alert">{err}</div>}
            <button disabled={busy} className="btn-primary-vs w-full h-12 font-semibold flex items-center justify-center gap-2" data-testid="kyc-submit-button">{busy && <Loader2 className="animate-spin" size={17} />}<ShieldCheck size={17} />Confirm & continue</button>
          </form>
          <div className="text-[12px] text-slate-400 mt-3 flex items-center gap-1.5"><ShieldCheck size={13} /> Entity details are encrypted and logged for audit purposes</div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

export function KycComplete() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [msg, setMsg] = useState("Confirming your verification…");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 12 && !cancelled; i++) {
        try {
          const { data } = await api.get("/kyc/status");
          if (data.kyc_status === "verified") { await refresh(); nav("/app"); return; }
          if (data.kyc_status === "requires_input") { setMsg("A check didn't pass. Redirecting…"); await refresh(); nav("/kyc"); return; }
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      setMsg("Still processing. You'll be unlocked automatically once verified.");
      await refresh();
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col"><TopNav />
      <div className="flex-1 flex items-start justify-center px-4 pt-24">
        <div className="card-vs p-8 max-w-[480px] w-full text-center" data-testid="kyc-complete">
          <div className="flex items-center justify-center gap-2 text-slate-600"><Loader2 className="animate-spin" /> {msg}</div>
          <button onClick={() => nav("/kyc")} className="btn-navy-vs h-10 px-4 mt-5 text-[13px] font-semibold">Back to verification</button>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email, origin_url: window.location.origin }); }
    catch { /* generic response either way */ }
    setSent(true); setBusy(false);
  };
  return (
    <div className="bg-[#F8FAFC] min-h-screen"><TopNav />
      <div className="mx-auto max-w-[480px] px-4 py-12">
        <div className="card-vs p-7">
          <div className="overflow-hidden"><Logo size="sm" /></div>
          <h1 className="font-display text-[26px] font-bold mt-4">Reset your password</h1>
          {sent ? (
            <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-[14px] text-emerald-800" data-testid="forgot-sent">
              If an account exists for that email, a reset link is on its way. Check your inbox (and spam) — the link expires in 60 minutes.
            </div>
          ) : (<>
            <p className="text-slate-500 text-[14px] mt-1">Enter your account email and we'll send you a secure reset link.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input className="w-full h-11 rounded-lg border border-slate-300 px-3" placeholder="Business email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
              <button disabled={busy} className="btn-primary-vs w-full h-12 font-semibold flex items-center justify-center gap-2" data-testid="forgot-submit-button">{busy && <Loader2 className="animate-spin" size={17} />}Send reset link</button>
            </form>
          </>)}
          <div className="text-[13px] text-slate-500 mt-4"><Link to="/login" className="text-[#174C82] font-semibold">Back to sign in</Link></div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

export function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false); const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw }); setDone(true); }
    catch (e2) { setErr(e2.response?.data?.detail || "Could not reset password"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#F8FAFC] min-h-screen"><TopNav />
      <div className="mx-auto max-w-[480px] px-4 py-12">
        <div className="card-vs p-7">
          <div className="overflow-hidden"><Logo size="sm" /></div>
          <h1 className="font-display text-[26px] font-bold mt-4">Choose a new password</h1>
          {!token ? (
            <div className="mt-4 text-[14px] text-red-700" data-testid="reset-no-token">This reset link is missing its token. Please request a new one.</div>
          ) : done ? (
            <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-[14px] text-emerald-800" data-testid="reset-done">
              Your password has been updated.
              <button onClick={() => nav("/login")} className="btn-primary-vs w-full h-11 mt-4 font-semibold">Sign in</button>
            </div>
          ) : (<form onSubmit={submit} className="mt-5 space-y-3">
            <input className="w-full h-11 rounded-lg border border-slate-300 px-3" placeholder="New password (min 8 chars)" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password-input" />
            <input className="w-full h-11 rounded-lg border border-slate-300 px-3" placeholder="Confirm new password" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm" />
            {err && <div className="text-red-600 text-[13px]" role="alert">{err}</div>}
            <button disabled={busy} className="btn-primary-vs w-full h-12 font-semibold flex items-center justify-center gap-2" data-testid="reset-submit-button">{busy && <Loader2 className="animate-spin" size={17} />}Update password</button>
          </form>)}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

export function ContactPage() {
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.post("/contact", f); setSent(true); }
    catch (e2) { setErr(e2.response?.data?.detail || "Could not send message"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#F8FAFC] min-h-screen"><TopNav />
      <div className="mx-auto max-w-[560px] px-4 py-12">
        <div className="card-vs p-7">
          <div className="text-[12px] font-bold tracking-widest text-[#174C82]">GET IN TOUCH</div>
          <h1 className="font-display text-[28px] font-bold mt-2">Contact ValueStackHQ</h1>
          <p className="text-slate-500 text-[14px] mt-1">Questions about valuations, pricing, or enterprise plans? Send us a message and we'll reply to <a href="mailto:contact@valuestackhq.com" className="text-[#174C82] font-semibold">contact@valuestackhq.com</a>.</p>
          {sent ? (
            <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-[14px] text-emerald-800" data-testid="contact-sent">
              Thanks — your message has been sent. We'll be in touch shortly.
            </div>
          ) : (<form onSubmit={submit} className="mt-5 space-y-3">
            <input className="w-full h-11 rounded-lg border border-slate-300 px-3" placeholder="Your name" value={f.name} onChange={set("name")} required data-testid="contact-name-input" />
            <input className="w-full h-11 rounded-lg border border-slate-300 px-3" placeholder="Your email" type="email" value={f.email} onChange={set("email")} required data-testid="contact-email-input" />
            <textarea className="w-full min-h-[130px] rounded-lg border border-slate-300 px-3 py-2" placeholder="How can we help?" value={f.message} onChange={set("message")} required data-testid="contact-message-input" />
            {err && <div className="text-red-600 text-[13px]" role="alert">{err}</div>}
            <button disabled={busy} className="btn-primary-vs w-full h-12 font-semibold flex items-center justify-center gap-2" data-testid="contact-submit-button">{busy && <Loader2 className="animate-spin" size={17} />}Send message</button>
          </form>)}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

export function PaymentResult({ mode }) {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  useEffect(() => {
    const sid = sp.get("session_id");
    if (mode === "cancel") { setState("cancel"); return; }
    if (!sid) { setState("error"); return; }
    (async () => {
      try {
        let paid = false;
        for (let i = 0; i < 12; i++) {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") { paid = true; break; }
          await new Promise((r) => setTimeout(r, 1500));
        }
        await refresh();
        setState(paid ? "done" : "pending");
      } catch { setState("error"); }
    })();
  }, []);
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col"><TopNav />
      <div className="flex-1 flex items-start justify-center px-4 pt-16">
      <div className="card-vs p-8 max-w-[480px] w-full text-center">
        {state === "done" ? (<><div className="text-emerald-600 font-bold text-[20px]">Payment confirmed ✓</div><p className="text-slate-500 text-[14px] mt-2">Entitlement unlocked. Run your full 5-method report.</p><button onClick={() => nav("/app")} className="btn-primary-vs w-full h-12 mt-5 font-semibold" data-testid="payment-go-dashboard-button">Go to dashboard</button></>)
        : state === "pending" ? (<><div className="font-bold text-[20px]">Payment received — finalizing</div><p className="text-slate-500 text-[14px] mt-2">Your payment is still being confirmed by your bank. Your access unlocks automatically the moment it clears — you can safely head to your dashboard.</p><button onClick={() => nav("/app")} className="btn-primary-vs w-full h-12 mt-5 font-semibold" data-testid="payment-go-dashboard-button">Go to dashboard</button></>)
        : state === "cancel" ? (<><div className="font-bold text-[20px]">Payment cancelled</div><button onClick={() => nav("/pricing")} className="btn-primary-vs w-full h-12 mt-5 font-semibold">Back to pricing</button></>)
        : state === "error" ? (<><div className="font-bold text-[20px]">Could not confirm payment</div><button onClick={() => nav("/pricing")} className="btn-primary-vs w-full h-12 mt-5 font-semibold">Back to pricing</button></>)
        : (<div className="flex items-center justify-center gap-2 text-slate-600"><Loader2 className="animate-spin" /> Confirming payment…</div>)}
      </div>
      </div>
      <SiteFooter />
    </div>
  );
}

export function ShareView() {
  const [sp] = useSearchParams();
  const token = window.location.pathname.split("/s/")[1];
  const [pw, setPw] = useState(""); const [data, setData] = useState(null); const [err, setErr] = useState("");
  const load = async () => {
    setErr("");
    try { const r = await api.get(`/share/${token}`, { params: pw ? { password: pw } : {} }); setData(r.data); }
    catch (e) { setErr(e.response?.data?.detail || "Link invalid"); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col"><TopNav />
      <div className="flex-1 px-4 pt-28 pb-10">
      <div className="mx-auto max-w-[640px]"><div className="overflow-hidden"><Logo size="sm" /></div>
        {!data ? (<div className="card-vs p-6 mt-6">
          <h1 className="font-display text-[22px] font-bold">Shared valuation</h1>
          {err && <div className="text-[13px] text-amber-700 mt-2">{err} — enter password if protected:</div>}
          <div className="flex gap-2 mt-3"><input className="flex-1 h-11 rounded-lg border border-slate-300 px-3" placeholder="Link password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="share-password-input" /><button onClick={load} className="btn-primary-vs h-11 px-5 font-semibold" data-testid="share-unlock-button">Unlock</button></div>
        </div>) : (<div className="card-vs p-6 mt-6">
          <div className="text-[12px] font-bold text-slate-500 tracking-widest">VERIFIED SHARE · {data.views} VIEWS</div>
          <h1 className="font-display text-[26px] font-bold mt-1">{data.company}</h1>
          <div className="text-[13px] text-slate-500">{data.sector} · {data.stage}</div>
          <div className="font-mono-num text-[28px] font-semibold text-[#174C82] mt-3" data-testid="share-range-value">{fmtM(data.range_low)} — {fmtM(data.range_high)}</div>
          <div className="text-[13px] text-slate-600">Midpoint <b className="font-mono-num">{fmtM(data.midpoint)}</b></div>
          <p className="text-[14px] text-slate-600 mt-3">{data.executive_summary}</p>
        </div>)}
      </div>
      </div>
      <SiteFooter />
    </div>
  );
}
